import { useMediaQuery } from "@uidotdev/usehooks";
import {
	addDays,
	addMonths,
	differenceInCalendarDays,
	format,
	parseISO,
} from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import {
	type EntryStatus,
	type TimelineEntry,
	timelineWindow,
} from "@/lib/therapist-relocation";
import { cn } from "@/lib/utils";

// Violet only: emerald, yellow and the rest of the status palette belong to the Visit lifecycle (DESIGN.md).
export const ROLE_TAG: Record<EntryStatus, string> = {
	CURRENT: "border-primary bg-primary text-primary-foreground",
	SCHEDULED: "border-dashed border-primary bg-primary/10 text-primary",
	ENDED: "border-border bg-muted text-muted-foreground",
};

// Go's days carry no time; parseISO reads them as local midnight, so no zone shifts the day.
export const day = (value: string) => parseISO(value);
export const shortCity = (city: string) => city.replace(/^KOTA /, "");

/** A booked visit from the edit page's upcomingVisits prop; `day` is its WIB day, YYYY-MM-DD. */
export type UpcomingVisit = {
	day: string;
	registrationNumber: string;
	visitNumber: number;
};

/**
 * The garis waktu (X12): one segment per Alamat Utama over a window of about four months back to two
 * after the latest start, today's line, and a dot per day with a booked visit.
 */
export function AddressTimeline({
	entries,
	today,
	upcomingVisits,
	describe,
	onPick,
}: {
	entries: TimelineEntry[];
	/** WIB calendar day, YYYY-MM-DD */
	today: string;
	upcomingVisits: UpcomingVisit[];
	/** The entry's dates, reason, and scheduler, for its segment's title. */
	describe: (entry: TimelineEntry) => string;
	onPick: (entry: TimelineEntry) => void;
}) {
	const { t } = useTranslation("therapists", { keyPrefix: "form.address" });
	const { locale } = useDateContext();
	const narrow = useMediaQuery("(max-width: 639px)");
	const [showAll, setShowAll] = useState(false);
	const fmt = (value: string) => format(day(value), "PP", { locale });

	// one dot per day, however many visits it holds
	const visitDays = [
		...upcomingVisits
			.reduce(
				(days, visit) =>
					days.set(visit.day, [...(days.get(visit.day) ?? []), visit]),
				new Map<string, UpcomingVisit[]>(),
			)
			.entries(),
	];

	const window = timelineWindow(entries, today, { narrow, showAll });
	const last = addDays(day(window.to), 1);
	const span = differenceInCalendarDays(last, day(window.from));
	const x = (value: Date) =>
		Math.max(
			0,
			Math.min(
				100,
				(differenceInCalendarDays(value, day(window.from)) / span) * 100,
			),
		);
	// window.from is the 1st of a month, so every later 1st inside the window gets a tick
	const months: Date[] = [];
	for (
		let month = addMonths(day(window.from), 1);
		month <= day(window.to);
		month = addMonths(month, 1)
	)
		months.push(month);

	return (
		<div className="grid gap-1">
			<div className="relative h-16">
				{entries.map((entry) => {
					const start =
						entry.effectiveFrom && entry.effectiveFrom > window.from
							? day(entry.effectiveFrom)
							: day(window.from);
					const end = entry.effectiveUntil
						? addDays(day(entry.effectiveUntil), 1)
						: last;
					if (end <= day(window.from)) return null;
					const width = x(end) - x(start);
					return (
						<button
							key={entry.therapistAddressId}
							type="button"
							title={`${entry.address.city} · ${describe(entry)}`}
							onClick={() => onPick(entry)}
							className={cn(
								"absolute top-4 h-7 truncate border px-2 text-left text-xs font-semibold uppercase",
								ROLE_TAG[entry.status],
							)}
							style={{ left: `${x(start)}%`, width: `${width}%` }}
						>
							{shortCity(entry.address.city)}
						</button>
					);
				})}
				<div
					className="absolute inset-y-0 w-px bg-foreground"
					style={{ left: `${x(day(today))}%` }}
				>
					<span className="absolute top-0 ml-1 text-[10px] font-semibold whitespace-nowrap">
						{t("today")}
					</span>
				</div>
				{visitDays.map(([visitDay, visits]) =>
					visitDay < window.from || visitDay > window.to ? null : (
						<span
							key={visitDay}
							title={`${fmt(visitDay)}: ${visits.map((visit) => `${visit.registrationNumber} · ${t("axis.visit", { number: visit.visitNumber })}`).join(", ")}`}
							className="absolute bottom-2 size-1.5 -translate-x-1/2 rounded-full bg-foreground/60"
							// the middle of its day
							style={{ left: `${x(day(visitDay)) + 50 / span}%` }}
						/>
					),
				)}
			</div>
			<div className="relative h-4 border-t">
				{months.map((month) => (
					<span
						key={month.toISOString()}
						className="absolute top-0.5 -translate-x-1/2 text-[10px] text-muted-foreground"
						style={{ left: `${x(month)}%` }}
					>
						{format(month, month.getMonth() === 0 ? "MMM yyyy" : "MMM", {
							locale,
						})}
					</span>
				))}
			</div>
			{visitDays.length > 0 && (
				<p className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<span className="rounded-full size-1.5 bg-foreground/60" />
					{t("axis.visits")}
				</p>
			)}
			{(window.hidden > 0 || showAll) && (
				<p className="text-xs text-muted-foreground">
					{window.hidden > 0 &&
						`${t("axis.hidden", { count: window.hidden })} `}
					<button
						type="button"
						className="font-medium underline text-primary underline-offset-4"
						onClick={() => setShowAll(!showAll)}
					>
						{showAll ? t("axis.compact") : t("axis.show_all")}
					</button>
				</p>
			)}
		</div>
	);
}
